import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { Row } from "@components/Flex";
import { SelectionInput, TextInput } from "@components/Input";
import { OnboardingLayout, OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { State } from "@ctypes";
import { useErrorBanner } from "@hooks";
import { Translations, i18n } from "@i18n";
import { constants } from "@layout";
import { convertToStateCode } from "@utils";
import { validationSchema } from "./content";
import { Separator } from "./styles";
import { AddressConfirmationFormValues, AddressFormProps } from "./types";

export const AddressForm = ({ data, onSubmit }: AddressFormProps) => {
  const { showErrorBanner } = useErrorBanner();

  const [loading, setLoading] = useState(false);

  const streetNumber = data?.streetNumber ?? "";
  const streetName = data?.streetName ?? "";
  const city = data?.city ?? "";
  const state = convertToStateCode(data?.state);
  const zipCode = data?.postalCode ?? "";
  const country = data?.country ?? "United States";

  const address = [streetNumber, streetName].join(" ").trim();

  const defaultStateIndex = Object.values(State).findIndex((s) => s === state);

  const {
    register,
    setValue,
    watch,
    handleSubmit: handleFormSubmit,
    formState: { errors },
  } = useForm<AddressConfirmationFormValues>({
    defaultValues: {
      address,
      address2: "",
      city,
      state,
      zipCode,
      country,
    },
    mode: "all",
    // @ts-expect-error: FIXME issue with address2 being nullable
    resolver: yupResolver(validationSchema),
  });

  const addressController = register("address");
  const address2Controller = register("address2");
  const cityController = register("city");
  const stateController = register("state");
  const zipCodeController = register("zipCode");
  const countryController = register("country");

  const handleSubmit = async (values: AddressConfirmationFormValues) => {
    try {
      setLoading(true);
      await onSubmit(values);
    } catch {
      showErrorBanner();
    } finally {
      setLoading(false);
    }
  };

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.AddressFormTitle),
    cta: {
      primary: {
        title: i18n.t(Translations.AddressFormPrimaryCTA),
        onClick: handleFormSubmit(handleSubmit),
        loading,
      },
    },
  };

  return (
    <OnboardingLayout {...props}>
      <TextInput
        {...addressController}
        label={i18n.t(Translations.AddressFormAddressLabel)}
        value={watch("address")}
        onChangeText={(value) => setValue("address", value, constants.form)}
        error={errors.address?.message}
      />
      <Spacing size={SpacingSizes.M} />

      <TextInput
        {...address2Controller}
        label={i18n.t(Translations.AddressFormUnitNoLabel)}
        value={watch("address2")}
        onChangeText={(value) => setValue("address2", value, constants.form)}
        error={errors.address2?.message}
      />
      <Spacing size={SpacingSizes.M} />

      <Row>
        <TextInput
          {...cityController}
          label={i18n.t(Translations.AddressFormCityLabel)}
          value={watch("city")}
          onChangeText={(value) => setValue("city", value, constants.form)}
          error={errors.city?.message}
        />

        <Separator />

        <SelectionInput
          {...stateController}
          label={i18n.t(Translations.AddressFormStateLabel)}
          items={Object.values(State)}
          defaultIndex={defaultStateIndex >= 0 ? defaultStateIndex : 0}
          onChangeSelection={(value) => {
            setValue("state", value, constants.form);
          }}
        />
      </Row>
      <Spacing size={SpacingSizes.M} />

      <TextInput
        {...zipCodeController}
        label={i18n.t(Translations.AddressFormZipCodeLabel)}
        value={watch("zipCode")}
        onChangeText={(value) => setValue("zipCode", value, constants.form)}
        error={errors.zipCode?.message}
      />
      <Spacing size={SpacingSizes.M} />

      <TextInput
        {...countryController}
        label={i18n.t(Translations.AddressFormCountryLabel)}
        value={watch("country")}
        onChangeText={(value) => setValue("country", value, constants.form)}
        error={errors.country?.message}
        readOnly
      />
      <Spacing size={SpacingSizes.M} />
    </OnboardingLayout>
  );
};
